import { z } from 'zod';
import { localizedStringSchema, optionalLocalizedStringSchema } from '../localized-string.js';
import { emailSchema, objectIdSchema, slugSchema, timeZoneSchema } from './common.js';
import { authProviderSchema } from './user.js';

// ---------------------------------------------------------------------------
// Availability schedules
// ---------------------------------------------------------------------------

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

/** "HH:MM" wall-clock time in the schedule's timezone. */
export const wallTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'invalid_time');

export const timeIntervalSchema = z
  .object({ start: wallTimeSchema, end: wallTimeSchema })
  .refine((v) => v.start < v.end, { message: 'interval_end_before_start' });
export type TimeInterval = z.infer<typeof timeIntervalSchema>;

function nonOverlapping(intervals: TimeInterval[]): boolean {
  const sorted = [...intervals].sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (prev && cur && cur.start < prev.end) return false;
  }
  return true;
}

export const intervalListSchema = z
  .array(timeIntervalSchema)
  .max(10)
  .refine(nonOverlapping, { message: 'intervals_overlap' });

export const weeklyHoursSchema = z.object(
  Object.fromEntries(WEEKDAYS.map((d) => [d, intervalListSchema])) as Record<
    Weekday,
    typeof intervalListSchema
  >,
);
export type WeeklyHours = z.infer<typeof weeklyHoursSchema>;

export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'invalid_date');

/**
 * Taking time out of your own week from the calendar screen.
 *
 * Times are optional: without them it is the whole day. They are wall-clock
 * strings in the schedule's own timezone, like every other hour in this file.
 */
export const blockTimeInputSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'invalid_date'),
    from: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
    to: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
  })
  .refine((v) => (v.from === undefined) === (v.to === undefined), {
    message: 'both or neither',
  })
  .refine((v) => !v.from || !v.to || v.from < v.to, { message: 'empty range' });
export type BlockTimeInput = z.infer<typeof blockTimeInputSchema>;

/**
 * The colours an event type can be, and the order they are handed out in.
 *
 * Every event type used to be the same near-black, which is fine on a list
 * where the name is right there and useless on a calendar, where the colour is
 * how a week reads as a shape before it reads as words. Dark enough for white
 * text, far enough apart to tell at a glance on a small block.
 */
export const DEFAULT_EVENT_TYPE_COLOUR = '#171717';

export const EVENT_TYPE_COLOURS = [
  '#171717', // near-black, the original
  '#1d4ed8', // blue
  '#047857', // green
  '#b45309', // amber
  '#7c3aed', // violet
  '#be123c', // rose
  '#0e7490', // teal
  '#4d7c0f', // olive
] as const;

/** The next colour for a business that already uses these. */
export function nextEventTypeColour(taken: string[]): string {
  const used = new Set(taken.map((c) => c.toLowerCase()));
  return (
    EVENT_TYPE_COLOURS.find((c) => !used.has(c)) ??
    // More event types than colours: start round again rather than refusing.
    (EVENT_TYPE_COLOURS[taken.length % EVENT_TYPE_COLOURS.length] as string)
  );
}

export const dateOverrideSchema = z.object({
  date: isoDateSchema,
  /** Empty list = unavailable that day. */
  intervals: intervalListSchema,
});
export type DateOverride = z.infer<typeof dateOverrideSchema>;

/** Countries whose public holidays we maintain ourselves (see packages/shared/data/holidays). */
export const HOLIDAY_COUNTRIES = ['JP'] as const;
export type HolidayCountry = (typeof HOLIDAY_COUNTRIES)[number];

export const holidaySchema = z.object({
  country: z.enum(HOLIDAY_COUNTRIES),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: localizedStringSchema,
});
export type Holiday = z.infer<typeof holidaySchema>;

export const availabilityScheduleInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  timezone: timeZoneSchema,
  weekly: weeklyHoursSchema,
  overrides: z.array(dateOverrideSchema).max(200).default([]),
  /** Public holidays of these countries are closed unless a date override opens them. */
  blockHolidays: z.array(z.enum(HOLIDAY_COUNTRIES)).max(10).default([]),
});
export type AvailabilityScheduleInput = z.infer<typeof availabilityScheduleInputSchema>;

export const availabilityScheduleSchema = availabilityScheduleInputSchema.extend({
  id: z.string(),
  isDefault: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AvailabilitySchedule = z.infer<typeof availabilityScheduleSchema>;

export const DEFAULT_WEEKLY_HOURS: WeeklyHours = {
  mon: [{ start: '09:00', end: '17:00' }],
  tue: [{ start: '09:00', end: '17:00' }],
  wed: [{ start: '09:00', end: '17:00' }],
  thu: [{ start: '09:00', end: '17:00' }],
  fri: [{ start: '09:00', end: '17:00' }],
  sat: [],
  sun: [],
};

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export const locationSchema = z.discriminatedUnion('kind', [
  /**
   * A video call, using whatever the person taking it has.
   *
   * The three kinds below name one tool, which is right when a business needs
   * its customers to know in advance. It is wrong when several people take the
   * same service and happen to use different tools: a member with Meet but no
   * Zoom cannot be put on a Zoom event type at all, however good they are at
   * the job.
   *
   * This defers the choice to whoever is assigned. The booking records what it
   * resolved to, so a booking is never ambiguous once it exists — only the list
   * of free times is, because nobody is assigned yet.
   */
  z.object({ kind: z.literal('video') }),
  z.object({ kind: z.literal('google_meet') }),
  z.object({ kind: z.literal('teams') }),
  /**
   * Zoom is unlike the two above it. Meet and Teams links arrive as a side
   * effect of writing the calendar event — we ask the provider for a
   * conference and it hands one back. Zoom knows nothing about the calendar,
   * so a booking here owns a meeting in the host's Zoom account that we create,
   * move and delete ourselves. That is why it needs its own connection.
   */
  z.object({ kind: z.literal('zoom') }),
  z.object({
    kind: z.literal('phone'),
    /** 'host_calls' = booker supplies a number; 'booker_calls' = we show the host's number. */
    direction: z.enum(['host_calls', 'booker_calls']),
    hostPhone: z.string().trim().max(40).optional(),
  }),
  z.object({ kind: z.literal('in_person'), address: z.string().trim().min(1).max(500) }),
  z.object({ kind: z.literal('custom'), text: localizedStringSchema }),
]);
export type Location = z.infer<typeof locationSchema>;

export const questionTypeSchema = z.enum(['text', 'textarea', 'select', 'checkbox', 'phone']);

export const questionSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9_-]{1,40}$/),
    label: localizedStringSchema,
    type: questionTypeSchema,
    required: z.boolean().default(false),
    options: z
      .array(z.object({ value: z.string().trim().min(1).max(100), label: localizedStringSchema }))
      .max(30)
      .default([]),
  })
  .refine((q) => q.type !== 'select' || q.options.length > 0, { message: 'select_needs_options' });
export type Question = z.infer<typeof questionSchema>;

export const refundPolicySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({
    kind: z.literal('full_before_hours'),
    fullRefundHours: z.number().int().min(0).max(720),
  }),
  z
    .object({
      kind: z.literal('tiered'),
      fullRefundHours: z.number().int().min(0).max(720),
      partialHours: z.number().int().min(0).max(720),
      partialPercent: z.number().int().min(1).max(99),
    })
    .refine((p) => p.partialHours < p.fullRefundHours, { message: 'partial_must_be_shorter' }),
]);
export type RefundPolicy = z.infer<typeof refundPolicySchema>;

export const afterBookingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('confirmation_page'), message: optionalLocalizedStringSchema }),
  z.object({ kind: z.literal('redirect'), redirectUrl: z.string().url().max(2000) }),
]);

export const priceSchema = z.object({
  amount: z.number().int().min(0).max(100_000_000),
  currency: z.string().length(3).toUpperCase(),
});

/**
 * How the event type looks when embedded on the host's own website. Colours are
 * null for "use the default"; the widget mixes every other shade from these.
 * Stored on the event type so the host changes them here, not in the snippet
 * pasted into their site.
 */
export const hexColourSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const embedSettingsSchema = z.object({
  showDetails: z.boolean().default(true),
  bg: hexColourSchema.nullable().default(null),
  fg: hexColourSchema.nullable().default(null),
  accent: hexColourSchema.nullable().default(null),
});
export type EmbedSettings = z.infer<typeof embedSettingsSchema>;
export const DEFAULT_EMBED_SETTINGS: EmbedSettings = {
  showDetails: true,
  bg: null,
  fg: null,
  accent: null,
};

/**
 * Who takes the meetings this event type offers.
 *
 * Empty means the account owner does, which is every event type until somebody
 * is named — and the shape the whole system had before workspace members.
 *
 * A role per entry rather than a bare list of ids, from the first version. The
 * distinction it exists for is Cal.com's: a *fixed* host attends every meeting
 * alongside whoever is rotating, which cannot be expressed by a list of ids
 * however long. Adding it later would mean migrating stored documents; adding
 * it now costs a field nobody reads yet.
 *
 * Capped at ten. Not a product limit so much as a floor under the cost of a
 * slot lookup, which reads a calendar per member of the pool: a page offering
 * times from fifty diaries would be slow in exactly the moment somebody is
 * deciding whether to book.
 */
export const eventTypeHostSchema = z.object({
  agentId: objectIdSchema,
  role: z.enum(['fixed', 'rotating']).default('rotating'),
});
export type EventTypeHost = z.infer<typeof eventTypeHostSchema>;

export const eventTypeInputSchema = z.object({
  hosts: z.array(eventTypeHostSchema).max(10).default([]),
  name: localizedStringSchema,
  description: optionalLocalizedStringSchema,
  slug: slugSchema,
  colour: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default(DEFAULT_EVENT_TYPE_COLOUR),
  durationMinutes: z.number().int().min(5).max(720),
  slotIntervalMinutes: z.number().int().min(5).max(720).optional(),
  bufferBeforeMin: z.number().int().min(0).max(120).default(0),
  bufferAfterMin: z.number().int().min(0).max(120).default(0),
  minNoticeMinutes: z.number().int().min(0).max(43_200).default(240),
  maxDaysAhead: z.number().int().min(1).max(365).default(60),
  dailyCap: z.number().int().min(0).max(100).default(0),
  location: locationSchema,
  questions: z.array(questionSchema).max(20).default([]),
  afterBooking: afterBookingSchema.default({ kind: 'confirmation_page' }),
  reminders: z.array(z.number().int().min(5).max(20_160)).max(5).default([1440]),
  /** How many times the booker may reschedule. 0 = never; hosts are never limited. */
  maxBookerReschedules: z.number().int().min(0).max(20).default(2),
  /** Booker may not reschedule once the booking is closer than this. Null = same as minimum notice. */
  rescheduleNoticeMinutes: z.number().int().min(0).max(43_200).nullable().default(null),
  /**
   * Whether the booker may call the appointment off themselves.
   *
   * Off is for a host who would rather be asked — most obviously on a paid
   * booking with no refund, where a self-service cancel button invites a
   * customer to end the booking and then expect their money back. The host can
   * always cancel, and turning this off never traps anyone: the page tells them
   * to get in touch instead.
   */
  bookerCanCancel: z.boolean().default(true),
  scheduleId: objectIdSchema,
  active: z.boolean().default(true),
  /**
   * `public` is listed on the host's profile and bookable from its own URL.
   * `invite_only` is neither: it is reachable only through a single-use link,
   * which is how a host replies to an enquiry without opening a public page.
   *
   * Separate from `active`, which still means "bookable at all". Turning an
   * event type off hides it from invitations too.
   */
  visibility: z.enum(['public', 'invite_only']).default('public'),
  /**
   * Null clears it. Undefined cannot: in a partial update an absent key means
   * "leave this alone", so a host who emptied the price field stayed on a paid
   * event type with no way back.
   */
  price: priceSchema.nullable().optional(),
  /**
   * The price is shown and recorded, but taken at the appointment rather than
   * through us: cash, the host's own terminal, a QR code, an invoice after.
   *
   * How a great deal of in-person business is actually done here, and it needs
   * no Stripe account, no subscription and no pending state, because nothing is
   * ever outstanding on our side.
   */
  payOnArrival: z.boolean().default(false),
  refundPolicy: refundPolicySchema.default({ kind: 'none' }),
  paymentDescription: optionalLocalizedStringSchema,
  embed: embedSettingsSchema.default(DEFAULT_EMBED_SETTINGS),
});
export type EventTypeInput = z.infer<typeof eventTypeInputSchema>;
/**
 * The same shape before defaults are applied, so a caller that means to accept
 * every default can send only the fields it has an opinion about instead of
 * restating them and risking drift from the schema.
 */
export type EventTypeInputDraft = z.input<typeof eventTypeInputSchema>;

export const eventTypeSchema = eventTypeInputSchema.extend({
  id: z.string(),
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type EventType = z.infer<typeof eventTypeSchema>;

// ---------------------------------------------------------------------------
// Calendar connections (host-facing DTOs; tokens never leave the API)
// ---------------------------------------------------------------------------

export const calendarProviderSchema = authProviderSchema;
export type CalendarProvider = z.infer<typeof calendarProviderSchema>;

export const connectedCalendarSchema = z.object({
  externalId: z.string(),
  name: z.string(),
  colour: z.string().nullable(),
  isPrimary: z.boolean(),
  canWrite: z.boolean(),
  checkConflicts: z.boolean(),
});
export type ConnectedCalendar = z.infer<typeof connectedCalendarSchema>;

export const calendarConnectionSchema = z.object({
  id: z.string(),
  provider: calendarProviderSchema,
  email: z.string(),
  displayName: z.string(),
  status: z.enum(['active', 'needs_reauth', 'disconnected']),
  lastError: z.string().nullable(),
  lastSyncAt: z.string().datetime().nullable(),
  calendars: z.array(connectedCalendarSchema),
  /** External id of the calendar bookings are written to, if this connection owns it. */
  writeCalendarExternalId: z.string().nullable(),
  /**
   * A consumer Microsoft account. Microsoft does not let these create Teams
   * meetings, so that location cannot be honoured for them.
   */
  personalAccount: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type CalendarConnection = z.infer<typeof calendarConnectionSchema>;

// ---------------------------------------------------------------------------
// Zoom connection (per host)
// ---------------------------------------------------------------------------

/**
 * One Zoom account per host, so this is a singleton rather than a list: unlike
 * calendars, there is nothing to gain from two and plenty to get wrong about
 * which one a meeting lands in.
 *
 * `needsReauth` is separate from "not connected" because the two ask different
 * things of the host. Not connected means "set this up"; needs reauth means
 * "the meetings you are already promising customers are not being created".
 */
export const zoomConnectionSchema = z.object({
  email: z.string(),
  displayName: z.string(),
  /** Zoom's own account type, so the UI can warn about the free plan's 40-minute cap. */
  planType: z.enum(['free', 'paid', 'unknown']),
  status: z.enum(['active', 'needs_reauth']),
  lastError: z.string().nullable(),
  connectedAt: z.string().datetime(),
});
export type ZoomConnection = z.infer<typeof zoomConnectionSchema>;

/** Null when the host has never connected Zoom, or disconnected it. */
export const zoomConnectionViewSchema = z.object({
  connection: zoomConnectionSchema.nullable(),
  /** False when the server has no Zoom credentials configured at all. */
  available: z.boolean(),
});
export type ZoomConnectionView = z.infer<typeof zoomConnectionViewSchema>;

export const updateCalendarConnectionSchema = z.object({
  calendars: z
    .array(z.object({ externalId: z.string(), checkConflicts: z.boolean() }))
    .max(100)
    .optional(),
  /** Set this connection's calendar as the single write target (clears any other). */
  writeCalendarExternalId: z.string().nullable().optional(),
});
export type UpdateCalendarConnectionInput = z.infer<typeof updateCalendarConnectionSchema>;

// ---------------------------------------------------------------------------
// Booking settings (per host)
// ---------------------------------------------------------------------------

/** Enough for a support inbox, a manager and a spare; not a mailing list. */
export const MAX_CC_EMAILS = 5;

export const bookingSettingsSchema = z.object({
  sendProviderInvites: z.boolean(),
  /**
   * Whether the people doing the appointments are reminded as well as the
   * customer.
   *
   * Null is the default and means "only when nothing else will": an
   * appointment written into the staff member's own calendar is already going
   * to remind them, and a second reminder by email is noise. True and false
   * are the host overriding that either way.
   */
  hostReminders: z.boolean().nullable(),
  paymentMethods: z.array(z.enum(['card', 'konbini', 'paypay'])),
  /**
   * Extra addresses copied on every booking email that goes to the host, for a
   * shared support or reception inbox. Never used for booker-facing mail: the
   * customer must not see an internal address.
   */
  ccEmails: z.array(emailSchema).max(MAX_CC_EMAILS),
  /**
   * Require a booker to enter a code sent to their address before a free
   * booking is accepted. Off by default: real friction, worth it only for a
   * host being targeted. Paid bookings are gated by Checkout instead.
   */
  verifyBookerEmail: z.boolean(),
});
export type BookingSettings = z.infer<typeof bookingSettingsSchema>;
export const DEFAULT_BOOKING_SETTINGS: BookingSettings = {
  sendProviderInvites: true,
  hostReminders: null,
  paymentMethods: ['card'],
  ccEmails: [],
  verifyBookerEmail: false,
};
